import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  LinearProgress,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import { CheckSquare, Paperclip, PencilSimple, Plus, TextAlignLeft, Trash } from '@phosphor-icons/react';
import { AdminDevAPI } from '../../../lib/apiAdmin';
import { ConfirmDeleteDialog } from '../../../components/common/ConfirmDeleteDialog';
import { useProfileDraft } from '../organizations/ProfileDrafts';
import type { DevTicket, DevTicketAttachment, DevTicketChecklistItem } from '../../../types/admin';
import { AttachmentGallery, FilePicker } from './TicketAttachments';
import { addDraftFiles, pastedFiles } from './ticketFiles';
import { TicketError, TicketMarkdown, TicketSection } from './ticketShared';

export function TicketDescription({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [baseVersion, setBaseVersion] = useState(ticket.updated_at);
  const [preview, setPreview] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<unknown>(null);
  const [latest, setLatest] = useState<DevTicket | null>(null);
  const reload = useMutation({
    mutationFn: () => AdminDevAPI.getTicket(ticket.id),
    onSuccess: setLatest,
  });
  const save = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description);
      form.append('expected_updated_at', baseVersion);
      files.forEach((file) => form.append('files', file));
      return AdminDevAPI.updateTicket(ticket.id, form);
    },
    onSuccess: () => {
      setEditing(false);
      setFiles([]);
      setLatest(null);
      refresh();
    },
  });
  useProfileDraft(
    'description',
    editing && (title !== ticket.title || description !== ticket.description || files.length > 0),
    save.isPending || reload.isPending,
  );
  return (
    <TicketSection
      title="Description"
      icon={<TextAlignLeft size={20} />}
      action={
        !editing ? (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            startDecorator={<PencilSimple />}
            onClick={() => {
              setTitle(ticket.title);
              setDescription(ticket.description);
              setBaseVersion(ticket.updated_at);
              setEditing(true);
              setLatest(null);
              save.reset();
            }}
          >
            Edit details
          </Button>
        ) : undefined
      }
    >
      {!editing ? (
        ticket.description ? (
          <TicketMarkdown text={ticket.description} />
        ) : (
          <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
            <Typography level="body-sm">
              Describe the problem, expected behavior, and what success looks like.
            </Typography>
            <Button
              size="sm"
              variant="plain"
              onClick={() => {
                setTitle(ticket.title);
                setDescription(ticket.description);
                setBaseVersion(ticket.updated_at);
                setLatest(null);
                save.reset();
                setEditing(true);
              }}
            >
              Add a description
            </Button>
          </Sheet>
        )
      ) : (
        <Box
          component="form"
          id={`dev-ticket-details-${ticket.id}`}
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim() && !save.isPending && !reload.isPending) save.mutate();
          }}
          sx={{ display: 'grid', gap: 1.5 }}
          onPaste={(event) => {
            const incoming = pastedFiles(event);
            if (incoming.length && !save.isPending) {
              try {
                setFiles(addDraftFiles(files, incoming));
                setFileError(null);
              } catch (error) {
                setFileError(error);
              }
            }
          }}
        >
          <FormControl>
            <FormLabel>Ticket title</FormLabel>
            <Input
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={save.isPending}
              slotProps={{ input: { maxLength: 255, pattern: '.*\\S.*', title: 'Enter a ticket title.' } }}
            />
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Chip size="sm" variant="soft">
              Markdown supported
            </Chip>
            <Button
              size="sm"
              variant={preview ? 'soft' : 'plain'}
              onClick={() => setPreview((value) => !value)}
            >
              {preview ? 'Continue editing' : 'Preview description'}
            </Button>
          </Box>
          {preview ? (
            <Sheet variant="outlined" sx={{ p: 2, minHeight: 160, borderRadius: 'sm' }}>
              <TicketMarkdown text={description || 'Your description preview will appear here.'} />
            </Sheet>
          ) : (
            <Textarea
              aria-label="Ticket description"
              minRows={7}
              value={description}
              disabled={save.isPending}
              placeholder="What happened? What should happen? Include steps to reproduce and acceptance criteria."
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
          <FilePicker files={files} onChange={setFiles} disabled={save.isPending} label="Attach to ticket" />
          <TicketError error={fileError || reload.error || save.error} />
          {save.isError && (
            <Button size="sm" variant="plain" loading={reload.isPending} onClick={() => reload.mutate()}>
              Review latest saved version
            </Button>
          )}
          {latest && (
            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
              <Typography level="title-sm">Latest saved details</Typography>
              <Typography level="body-sm" sx={{ my: 1 }}>
                {latest.title}
              </Typography>
              <TicketMarkdown text={latest.description || 'No description yet.'} />
              <Typography level="body-sm" sx={{ my: 1 }}>
                Your draft is still in the editor above. Review and combine any changes before saving.
              </Typography>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  setBaseVersion(latest.updated_at);
                  setLatest(null);
                  save.reset();
                  refresh();
                }}
              >
                I reviewed this version — keep my draft
              </Button>
            </Sheet>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography level="body-xs">Use Save details below to save your changes.</Typography>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              disabled={save.isPending}
              onClick={() => {
                setEditing(false);
                setFiles([]);
                setFileError(null);
                setLatest(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </TicketSection>
  );
}

export function TicketFiles({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<unknown>(null);
  const [removing, setRemoving] = useState<DevTicketAttachment | null>(null);
  const upload = useMutation({
    mutationFn: () => AdminDevAPI.uploadAttachments(ticket.id, files),
    onSuccess: () => {
      setFiles([]);
      setFileError(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => AdminDevAPI.removeAttachment(ticket.id, removing!.id),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });
  useProfileDraft('attachments', files.length > 0, upload.isPending || remove.isPending);
  return (
    <TicketSection title={`Attachments (${ticket.attachments?.length ?? 0})`} icon={<Paperclip size={20} />}>
      <Sheet
        variant="outlined"
        sx={{ p: 1.5, borderStyle: 'dashed', borderRadius: 'md' }}
        onPaste={(event) => {
          const incoming = pastedFiles(event);
          if (incoming.length && !upload.isPending) {
            try {
              setFiles(addDraftFiles(files, incoming));
              setFileError(null);
            } catch (error) {
              setFileError(error);
            }
          }
        }}
      >
        <FilePicker files={files} onChange={setFiles} disabled={upload.isPending} />
        <TicketError error={fileError || upload.error} />
        {files.length > 0 && (
          <Button size="sm" loading={upload.isPending} onClick={() => upload.mutate()}>
            Upload {files.length} file{files.length === 1 ? '' : 's'}
          </Button>
        )}
      </Sheet>
      <AttachmentGallery
        files={ticket.attachments ?? []}
        disabled={remove.isPending}
        onRemove={(file) => {
          remove.reset();
          setRemoving(file);
        }}
      />
      <ConfirmDeleteDialog
        open={!!removing}
        label="Delete attachment"
        pending={remove.isPending}
        error={remove.error}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove.mutate()}
      >
        <Typography>Delete {removing?.name}? This also removes it from any comment it belongs to.</Typography>
      </ConfirmDeleteDialog>
    </TicketSection>
  );
}
function ChecklistRow({
  ticketId,
  item,
  refresh,
}: {
  ticketId: number;
  item: DevTicketChecklistItem;
  refresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [deleting, setDeleting] = useState(false);
  const save = useMutation({
    mutationFn: (changes: Partial<DevTicketChecklistItem>) =>
      AdminDevAPI.updateChecklistItem(ticketId, item.id, changes),
    onSuccess: async () => {
      setEditing(false);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => AdminDevAPI.deleteChecklistItem(ticketId, item.id),
    onSuccess: refresh,
  });
  useProfileDraft(`checklist-${item.id}`, editing && text !== item.text, save.isPending || remove.isPending);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
        <Checkbox
          slotProps={{ input: { 'aria-label': `Complete ${item.text}` } }}
          checked={
            save.isPending && typeof save.variables?.is_done === 'boolean'
              ? save.variables.is_done
              : item.is_done
          }
          disabled={save.isPending}
          onChange={(event) => save.mutate({ is_done: event.target.checked })}
        />
        {editing ? (
          <Box
            component="form"
            sx={{ display: 'flex', flex: 1, gap: 1, minWidth: 0 }}
            onSubmit={(event) => {
              event.preventDefault();
              if (text.trim()) save.mutate({ text: text.trim() });
            }}
          >
            <Input
              size="sm"
              aria-label="Checklist item text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Button type="submit" size="sm" loading={save.isPending} disabled={!text.trim()}>
              Save
            </Button>
            <Button size="sm" variant="plain" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </Box>
        ) : (
          <>
            <Typography
              level="body-sm"
              sx={{
                flex: 1,
                overflowWrap: 'anywhere',
                textDecoration: item.is_done ? 'line-through' : 'none',
                color: item.is_done ? 'text.tertiary' : 'text.primary',
              }}
            >
              {item.text}
            </Typography>
            <IconButton
              size="sm"
              aria-label={`Edit checklist item ${item.text}`}
              onClick={() => {
                setText(item.text);
                setEditing(true);
              }}
            >
              <PencilSimple size={15} />
            </IconButton>
            <IconButton
              size="sm"
              aria-label={`Delete checklist item ${item.text}`}
              onClick={() => setDeleting(true)}
            >
              <Trash size={15} />
            </IconButton>
          </>
        )}
      </Box>
      <TicketError error={save.error} />
      <ConfirmDeleteDialog
        open={deleting}
        label="Delete checklist item"
        pending={remove.isPending}
        error={remove.error}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
      >
        <Typography>{item.text}</Typography>
      </ConfirmDeleteDialog>
    </Box>
  );
}
export function TicketChecklist({ ticket, refresh }: { ticket: DevTicket; refresh: () => void }) {
  const [text, setText] = useState('');
  const items = ticket.checklist ?? [];
  const completed = items.filter((item) => item.is_done).length;
  const add = useMutation({
    mutationFn: () => AdminDevAPI.addChecklistItem(ticket.id, text.trim()),
    onSuccess: () => {
      setText('');
      refresh();
    },
  });
  useProfileDraft('new-checklist-item', !!text.trim(), add.isPending);
  return (
    <TicketSection
      title="Checklist"
      icon={<CheckSquare size={20} />}
      action={
        <Chip
          size="sm"
          variant="soft"
          color={items.length && completed === items.length ? 'success' : 'neutral'}
        >
          {completed}/{items.length} complete
        </Chip>
      }
    >
      {items.length > 0 && (
        <LinearProgress
          determinate
          value={(completed * 100) / items.length}
          color="success"
          aria-label="Checklist completion"
          sx={{ mb: 2 }}
        />
      )}
      <Box sx={{ display: 'grid', gap: 1 }}>
        {items.map((item) => (
          <ChecklistRow key={item.id} ticketId={ticket.id} item={item} refresh={refresh} />
        ))}
      </Box>
      <Box
        component="form"
        sx={{ display: 'flex', gap: 1, mt: 1.5 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim() && !add.isPending) add.mutate();
        }}
      >
        <Input
          size="sm"
          placeholder="Add an acceptance criterion or next step"
          aria-label="New checklist item"
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={add.isPending}
          slotProps={{ input: { maxLength: 255 } }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button
          type="submit"
          size="sm"
          variant="outlined"
          startDecorator={<Plus size={15} />}
          disabled={!text.trim()}
          loading={add.isPending}
        >
          Add
        </Button>
      </Box>
      <TicketError error={add.error} />
    </TicketSection>
  );
}
